import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  HiOutlineRefresh, HiOutlineCheckCircle, HiOutlineXCircle,
  HiOutlineServer, HiOutlineSearch,
  HiOutlineChevronDown, HiOutlineChevronRight, HiOutlineDownload,
} from 'react-icons/hi';
import { getAllVMInstances, getServiceTags, setServiceTag } from '../services/api';
import ServiceTagDropdown, { groupAndSort, getTagLabel, TagFilter, stickyTh, stickyTd, stickyGroupTd } from '../components/ServiceTagDropdown';
import './CloudScheduler.css';

const SERVICE_TYPE = 'vm';

const VM_PRICING_INR = {
  'e2-micro': 596, 'e2-small': 1192, 'e2-medium': 2384,
  'e2-standard-2': 4768, 'e2-standard-4': 9536, 'e2-standard-8': 19072,
  'n1-standard-1': 2156, 'n1-standard-2': 4312, 'n1-standard-4': 8624,
  'f1-micro': 330, 'g1-small': 1050,
};

function estimateVMCost(vm) {
  const mt = vm.machineType || '';
  const price = VM_PRICING_INR[mt];
  if (price) return vm.status === 'RUNNING' ? price : 0;
  return null;
}

function formatCost(cost) {
  if (cost === null || cost === undefined) return '—';
  if (cost === 0) return <span className="cost-cell"><span className="cost-approx">~</span> ₹0</span>;
  return <span className="cost-cell"><span className="cost-approx">~</span> ₹{cost.toLocaleString('en-IN')}</span>;
}

function escapeCsvField(val) {
  const str = String(val ?? '');
  if (str.includes(',') || str.includes('"') || str.includes('\n')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function vmStatusBadge(status) {
  if (status === 'RUNNING') return <span className="status-badge success"><HiOutlineCheckCircle size={12} /> Running</span>;
  if (status === 'TERMINATED' || status === 'STOPPED') return <span className="status-badge error"><HiOutlineXCircle size={12} /> {status === 'TERMINATED' ? 'Terminated' : 'Stopped'}</span>;
  if (status === 'STAGING' || status === 'PROVISIONING') return <span className="status-badge warning">{status.charAt(0) + status.slice(1).toLowerCase()}</span>;
  if (status === 'SUSPENDED') return <span className="status-badge neutral">Suspended</span>;
  return <span className="status-badge neutral">{status || 'Unknown'}</span>;
}

function VMInstances({ gcpProjects, selectedProjectId }) {
  const [instances, setInstances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedRows, setExpandedRows] = useState({});
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [tagMap, setTagMap] = useState({});
  const [tagFilter, setTagFilter] = useState('all');

  const activeProject = gcpProjects.find(p => p._id === selectedProjectId);

  const fetchInstances = useCallback(async (signal) => {
    if (!activeProject) return;
    setLoading(true);
    try {
      const res = await getAllVMInstances(activeProject._id);
      if (!signal?.cancelled) setInstances(res.data || []);
    } catch (err) {
      if (!signal?.cancelled) {
        toast.error('Failed to load VM instances: ' + (err.response?.data?.error || err.message));
        setInstances([]);
      }
    } finally {
      if (!signal?.cancelled) setLoading(false);
    }
  }, [activeProject]);

  const fetchTags = useCallback(async () => {
    if (!activeProject) return;
    try {
      const res = await getServiceTags(activeProject._id);
      const map = {};
      for (const [key, val] of Object.entries(res.data || {})) {
        if (key.startsWith(SERVICE_TYPE + ':')) {
          map[key.replace(SERVICE_TYPE + ':', '')] = val;
        }
      }
      setTagMap(map);
    } catch { /* tags are optional */ }
  }, [activeProject]);

  useEffect(() => {
    setInstances([]);
    setExpandedRows({});
    setTagMap({});
    if (!activeProject) return;
    const signal = { cancelled: false };
    fetchInstances(signal);
    fetchTags();
    return () => { signal.cancelled = true; };
  }, [activeProject, fetchInstances, fetchTags]);

  const handleTagChange = async (serviceName, tag) => {
    const prev = { ...tagMap };
    setTagMap(m => tag ? { ...m, [serviceName]: tag } : (() => { const n = { ...m }; delete n[serviceName]; return n; })());
    try {
      await setServiceTag(activeProject._id, SERVICE_TYPE, serviceName, tag);
    } catch {
      setTagMap(prev);
      toast.error('Failed to update tag');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <span className="sort-hint">⇅</span>;
    return <span className="sort-active">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const sortFn = useCallback((list) => {
    const mul = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortField === 'name') return mul * (a.name || '').localeCompare(b.name || '');
      if (sortField === 'zone') return mul * (a.zone || '').localeCompare(b.zone || '');
      if (sortField === 'status') return mul * (a.status || '').localeCompare(b.status || '');
      if (sortField === 'machineType') return mul * (a.machineType || '').localeCompare(b.machineType || '');
      return 0;
    });
  }, [sortField, sortDir]);

  const grouped = useMemo(() => {
    let list = instances;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(vm =>
        vm.name.toLowerCase().includes(q) ||
        vm.zone.toLowerCase().includes(q) ||
        (vm.status || '').toLowerCase().includes(q) ||
        (vm.machineType || '').toLowerCase().includes(q) ||
        (vm.internalIp || '').includes(q) ||
        (vm.externalIp || '').includes(q)
      );
    }
    return groupAndSort(list, tagMap, 'name', sortFn, tagFilter);
  }, [instances, search, tagMap, sortFn, tagFilter]);

  const flatFiltered = useMemo(() => grouped.filter(r => !r._groupHeader), [grouped]);

  const toggleRow = (key) => setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));

  const COL_COUNT = 9;

  const downloadCSV = () => {
    if (flatFiltered.length === 0) return;
    const headers = ['Tag', 'Name', 'Zone', 'Status', 'Machine Type', 'Est. Cost (INR)', 'Internal IP', 'External IP'];
    const rows = flatFiltered.map(vm => [
      escapeCsvField(getTagLabel(tagMap[vm.name]) || ''),
      escapeCsvField(vm.name),
      escapeCsvField(vm.zone),
      escapeCsvField(vm.status),
      escapeCsvField(vm.machineType || ''),
      escapeCsvField(estimateVMCost(vm) ?? ''),
      escapeCsvField(vm.internalIp || ''),
      escapeCsvField(vm.externalIp || ''),
    ]);
    const csvContent = [
      headers.map(escapeCsvField).join(','),
      ...rows.map(r => r.join(',')),
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vm-instances-${activeProject?.projectId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!activeProject) {
    return (
      <div className="cloud-scheduler page-container">
        <div className="gcp-page-header">
          <div className="gcp-page-title-row"><h1><HiOutlineServer size={28} /> VM Instances</h1></div>
        </div>
        <div className="empty-state"><p>Select a GCP project to view VM Instances.</p></div>
      </div>
    );
  }

  return (
    <div className="cloud-scheduler page-container">
      <div className="gcp-page-header">
        <div className="gcp-page-title-row">
          <h1><HiOutlineServer size={28} /> VM Instances</h1>
          <span className="pv-subtitle">{activeProject.projectName} — {flatFiltered.length} instance{flatFiltered.length !== 1 ? 's' : ''}</span>
        </div>
        <div className="gcp-page-toolbar">
          <div className="header-search">
            <HiOutlineSearch size={14} />
            <input type="text" placeholder="Search instances..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <TagFilter value={tagFilter} onChange={setTagFilter} />
          <button className="btn btn-ghost btn-sm" onClick={downloadCSV} disabled={loading || flatFiltered.length === 0} title="Download CSV">
            <HiOutlineDownload size={14} /> CSV
          </button>
          <button className="btn btn-ghost btn-sm" onClick={() => fetchInstances({})} disabled={loading}>
            <HiOutlineRefresh size={14} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {loading && <div className="loading-bar"><div className="loading-bar-inner" /></div>}

      {!loading && instances.length === 0 && (
        <div className="empty-state"><p>No VM instances found in <strong>{activeProject.projectName}</strong>.</p></div>
      )}

      {grouped.length > 0 && (
        <div className="resource-table-wrap">
          <div className="resource-table-scroll">
          <table className="resource-table">
            <thead>
              <tr>
                <th style={stickyTh[0]}></th>
                <th style={stickyTh[1]}>Tag</th>
                <th className="sortable-th" style={stickyTh[2]} onClick={() => handleSort('name')}>Name <SortIcon field="name" /></th>
                <th className="sortable-th" onClick={() => handleSort('zone')}>Zone <SortIcon field="zone" /></th>
                <th className="sortable-th" onClick={() => handleSort('status')}>Status <SortIcon field="status" /></th>
                <th className="sortable-th" onClick={() => handleSort('machineType')}>Machine Type <SortIcon field="machineType" /></th>
                <th>Est. Cost</th>
                <th>Internal IP</th>
                <th>External IP</th>
              </tr>
            </thead>
            <tbody>
              {grouped.map((row, i) => {
                if (row._groupHeader) {
                  return (
                    <tr key={`gh-${row.group}`} className="group-header-row">
                      <td style={stickyGroupTd[0]}></td>
                      <td style={stickyGroupTd[1]}></td>
                      <td style={stickyGroupTd[2]}>
                        <span className="group-label">
                          <span className={`group-dot ${row.group}`}></span>
                          {row.label}
                          <span className="group-count">({row.count})</span>
                        </span>
                      </td>
                      <td colSpan={COL_COUNT - 3}></td>
                    </tr>
                  );
                }
                const vm = row;
                const key = vm.id || `${vm.name}-${vm.zone}`;
                const isOpen = expandedRows[key];
                const cost = estimateVMCost(vm);
                return (
                  <React.Fragment key={key}>
                    <tr className="expandable-row" onClick={() => toggleRow(key)}>
                      <td className="expand-toggle" style={stickyTd[0]}>
                        {isOpen ? <HiOutlineChevronDown size={14} /> : <HiOutlineChevronRight size={14} />}
                      </td>
                      <td style={stickyTd[1]}>
                        <ServiceTagDropdown value={tagMap[vm.name] || ''} onChange={(tag) => handleTagChange(vm.name, tag)} />
                      </td>
                      <td className="cell-name" style={stickyTd[2]}>{vm.name}</td>
                      <td><span className="region-badge">{vm.zone}</span></td>
                      <td>{vmStatusBadge(vm.status)}</td>
                      <td><code>{vm.machineType || '—'}</code></td>
                      <td>{formatCost(cost)}</td>
                      <td>{vm.internalIp || '—'}</td>
                      <td>{vm.externalIp || '—'}</td>
                    </tr>
                    {isOpen && (
                      <tr className="expand-detail-row">
                        <td colSpan={COL_COUNT}>
                          <div className="expand-detail">
                            <div className="detail-grid">
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Instance ID</span>
                                  <span className="detail-value mono">{vm.id}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Zone</span>
                                  <span className="detail-value">{vm.zone}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Region</span>
                                  <span className="detail-value">{vm.region}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Machine Type</span>
                                  <span className="detail-value mono">{vm.machineType || '—'}</span>
                                </div>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">CPU Platform</span>
                                  <span className="detail-value">{vm.cpuPlatform || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Internal IP</span>
                                  <span className="detail-value mono">{vm.internalIp || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">External IP</span>
                                  <span className="detail-value mono">{vm.externalIp || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Created</span>
                                  <span className="detail-value">{vm.creationTimestamp ? new Date(vm.creationTimestamp).toLocaleString() : '—'}</span>
                                </div>
                              </div>
                              <div className="detail-row">
                                <div className="detail-item">
                                  <span className="detail-label">Network</span>
                                  <span className="detail-value">{vm.network || '—'}</span>
                                </div>
                                <div className="detail-item">
                                  <span className="detail-label">Subnet</span>
                                  <span className="detail-value">{vm.subnet || '—'}</span>
                                </div>
                              </div>
                              {vm.serviceAccounts?.length > 0 && (
                                <div className="detail-item full-width">
                                  <span className="detail-label">Service Accounts</span>
                                  <span className="detail-value mono">{vm.serviceAccounts.join(', ')}</span>
                                </div>
                              )}
                              {vm.disks?.length > 0 && (
                                <>
                                  <div className="detail-divider">Disks ({vm.disks.length})</div>
                                  {vm.disks.map((d, di) => (
                                    <div className="detail-row" key={di}>
                                      <div className="detail-item">
                                        <span className="detail-label">{d.boot ? 'Boot Disk' : 'Disk'}</span>
                                        <span className="detail-value">{d.name || '—'}</span>
                                      </div>
                                      <div className="detail-item">
                                        <span className="detail-label">Size</span>
                                        <span className="detail-value">{d.sizeGb ? `${d.sizeGb} GB` : '—'}</span>
                                      </div>
                                      <div className="detail-item">
                                        <span className="detail-label">Type</span>
                                        <span className="detail-value">{d.type || '—'}</span>
                                      </div>
                                    </div>
                                  ))}
                                </>
                              )}
                              {vm.tags?.length > 0 && (
                                <div className="detail-item full-width">
                                  <span className="detail-label">Network Tags</span>
                                  <span className="detail-value">{vm.tags.join(', ')}</span>
                                </div>
                              )}
                              {vm.labels && Object.keys(vm.labels).length > 0 && (
                                <div className="detail-item full-width">
                                  <span className="detail-label">Labels</span>
                                  <span className="detail-value mono">{Object.entries(vm.labels).map(([k, v]) => `${k}=${v}`).join(', ')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {!loading && instances.length > 0 && flatFiltered.length === 0 && (
        <div className="empty-state"><p>No instances match "<strong>{search}</strong>"</p></div>
      )}
    </div>
  );
}

export default VMInstances;
